import React, {PureComponent} from "react";
import Typography from "@mui/material/Typography";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import {AttributeValuesPrettier} from "../../../core/prettifier/AttributeValuesPrettier";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import {UrlBuilder} from "../../../core/url/UrlBuilder";
import moment from "moment";
import {UserLocalStorage} from "../../../core/storage/UserLocalStorage";
import {Button, TextField} from "@mui/material";
import {HoursMinutesTimeConverter} from "../../../core/prettifier/HoursMinutesTimeConverter";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import {LocalizationProvider, TimePicker} from "@mui/lab";
import LinearProgress from "@mui/material/LinearProgress";
import Fade from "@mui/material/Fade";
import Popover from "@mui/material/Popover";
import MessageIcon from "@mui/icons-material/Message";
import Backdrop from "@mui/material/Backdrop";
import Badge from "@mui/material/Badge";
import {ExerciseTypeAttribute} from "../../../core/models/ExerciseTypeAttribute";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

class WorkoutPlanCard extends PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			exercise: props.exercise,
			plannedLogs: UserLocalStorage.get(UrlBuilder.routine.routineExercisePlannedLogsApi(props.exercise.routineExerciseId)) ?? null,
			editablePlannedLogId: null,
			isNewPlannedLogBeingAdded: false,
			isLoading: false,
			logComment: null,
			dropDownCommentAnchorEl: null,
			logCommentId: null,
			infoPopoverAnchorEl: null,
		};
	}

	componentDidMount() {
		this.fetch().then(() => {
			// Auto-initialize add-row values when plan is empty
			if (!this.state.plannedLogs || this.state.plannedLogs.length === 0) {
				this.enableNewPlannedLogBeingAdded();
			}
		});
	}

	componentWillReceiveProps(nextProps, nextContext) {
		this.setState({ exercise: nextProps.exercise });
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		this.props.updateSwipeableViewsHeightFunc();
	}

	enableNewPlannedLogBeingAdded = () => {
		let state = {
			isNewPlannedLogBeingAdded: true,
			editablePlannedLogId: null
		};

		const lastPlannedLog = this.state.plannedLogs?.length > 0
			? this.state.plannedLogs[this.state.plannedLogs.length - 1]
			: null;

		const exercise = this.state.exercise;
		exercise.type.attributes.forEach((attribute, idx) => {
			if (lastPlannedLog) {
				state[attribute.key] = lastPlannedLog.values[idx];
			} else if (exercise.logGroups?.[0]?.logs?.[0]) {
				state[attribute.key] = exercise.logGroups[0].logs[0].values[idx];
			} else {
				state[attribute.key] = 0;
			}
		});

		this.setState(state);
	}

	setEditablePlannedLog = (e, plannedLog) => {
		e.stopPropagation();

		let state = {
			editablePlannedLogId: plannedLog.id,
			isNewPlannedLogBeingAdded: false,
		};

		let idx = 0;
		for (const attribute of this.state.exercise.type.attributes) {
			state[attribute.key] = plannedLog.values[idx];
			idx++;
		}

		this.setState(state);
	}

	fetch = async () => {
		this.setState({ isLoading: true });

		let response = await fetch(UrlBuilder.routine.routineExercisePlannedLogsApi(this.state.exercise.routineExerciseId));
		response = await response.json();
		const plannedLogs = response['data'];

		this.setState({
			plannedLogs: plannedLogs,
			isLoading: false
		});

		UserLocalStorage.set(UrlBuilder.routine.routineExercisePlannedLogsApi(this.state.exercise.routineExerciseId), plannedLogs ?? []);
		this.props.onPlannedLogsChanged?.(plannedLogs ?? []);
	}

	isAcceptableInput = (numberString) => {
		let number = Number(numberString);
		return !isNaN(number) && number >= 0;
	}

	submitWithEnter = (e) => {
		if (e.key === 'Enter' && this.canSubmit()) {
			if (this.state.editablePlannedLogId) {
				const plannedLog = this.state.plannedLogs.find(log => log.id === this.state.editablePlannedLogId);
				if (plannedLog) {
					this.submitEditedPlannedLog();
				}
			} else {
				this.submitNewPlannedLog();
			}
		}
	}

	canSubmit = () => {
		for (const attribute of this.state.exercise.type.attributes) {
			if (!this.isAcceptableInput(this.state[attribute.key])) {
				return false;
			}
		}
		return true;
	}

	submitNewPlannedLog = async () => {
		this.setState({ isLoading: true });

		let values = [];
		for (const attribute of this.state.exercise.type.attributes) {
			values.push(this.state[attribute.key]);
		}

		let plannedLogToSave = {
			routineExerciseId: this.state.exercise.routineExerciseId,
			dateCreated: moment().format('L, h:mm:ss A'),
			comment: this.state.logComment,
			values: values,
		};

		await fetch(UrlBuilder.routine.routineExercisePlannedLogsApi(), {
			method: 'POST',
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(plannedLogToSave)
		});

		this.setState({
			isNewPlannedLogBeingAdded: false,
			logComment: null,
		});

		await this.fetch();
	}

	submitEditedPlannedLog = async () => {
		this.setState({ isLoading: true });

		let values = [];
		for (const attribute of this.state.exercise.type.attributes) {
			values.push(this.state[attribute.key]);
		}

		let plannedLogToSave = {
			id: this.state.editablePlannedLogId,
			values: values,
		};

		await fetch(UrlBuilder.routine.routineExercisePlannedLogsApi(), {
			method: 'PUT',
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(plannedLogToSave)
		});

		await this.fetch();

		this.setState({ editablePlannedLogId: null });
	}

	deletePlannedLog = async () => {
		this.setState({ isLoading: true });

		await fetch(UrlBuilder.routine.routineExercisePlannedLogsApi(this.state.editablePlannedLogId), {
			method: 'DELETE',
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			}
		});

		this.setState({ editablePlannedLogId: null });

		await this.fetch();
	}

	updateTimeAttributeValue = (val) => {
		this.setState({
			time: (HoursMinutesTimeConverter.convertDateStringToFloat(val)),
		});
	}

	updateStateInputValues = (event) => {
		this.setState({ [event.target.name]: event.target.value });
	}

	renderInputField = (attribute) => {
		if (attribute.key === ExerciseTypeAttribute.Time) {
			return (
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<TimePicker
						ampm={false}
						openTo="minutes"
						views={["hours", "minutes", "seconds"]}
						inputFormat="HH:mm:ss"
						value={HoursMinutesTimeConverter.convertFloatToDateTime(this.state[attribute.key])}
						onChange={(val) => this.updateTimeAttributeValue(val)}
						renderInput={(params) => <TextField {...params} size="small" sx={{ maxWidth: 91 }} />}
					/>
				</LocalizationProvider>
			);
		}

		return (
			<TextField
				variant="outlined"
				type="number"
				onKeyPress={this.submitWithEnter}
				inputMode="decimal"
				required
				name={attribute.key}
				value={this.state[attribute.key]}
				onChange={this.updateStateInputValues}
				sx={{ maxWidth: 64 }}
				size="small"
			/>
		);
	}

	setSelectedLog = (plannedLog) => {
		if (this.state.editablePlannedLogId === plannedLog.id) {
			return;
		}
		this.props.setSelectedLogParentFunc(plannedLog);
	}

	openDropDownCommentAnchorEl = (event, logComment = null, logId = null) => {
		event.stopPropagation();
		this.setState({
			dropDownCommentAnchorEl: event.currentTarget,
			logComment: logComment ?? null,
			logCommentId: logId ?? null,
		});
	};

	closeDropDownCommentAnchorEl = async () => {
		let log = this.state.plannedLogs?.find(log => log.id === this.state.logCommentId);

		if (log) {
			if (this.state.logComment != log?.comment) {
				this.setState({ isLoading: true });

				let plannedLogToSave = {
					Id: log.id,
					comment: this.state.logComment,
				};

				await fetch(UrlBuilder.routine.routineExercisePlannedLogsApi(), {
					method: 'PUT',
					headers: {
						'Accept': 'application/json',
						'Content-Type': 'application/json'
					},
					body: JSON.stringify(plannedLogToSave)
				});

				await this.fetch();
			}

			this.setState({
				dropDownCommentAnchorEl: null,
				logComment: null,
				logCommentId: null,
			});
			return;
		}

		this.setState({
			dropDownCommentAnchorEl: null,
			logCommentId: null,
		});
	};

	updateLogComment = (event) => {
		this.setState({ logComment: event.target.value });
	}

	renderCommentButton = (comment, logId, event) => (
		<IconButton
			size="small"
			onClick={(e) => this.openDropDownCommentAnchorEl(e, comment, logId)}
			sx={{ color: comment ? 'secondary.main' : 'text.disabled' }}
		>
			{comment ? (
				<Badge color="secondary" variant="dot">
					<MessageIcon sx={{ fontSize: 18 }} />
				</Badge>
			) : (
				<MessageIcon sx={{ fontSize: 18 }} />
			)}
		</IconButton>
	)

	renderActionButtons = (plannedLog) => {
		const isEditing = this.state.editablePlannedLogId === plannedLog.id;

		if (isEditing) {
			return (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, justifyContent: 'flex-end' }}>
					<IconButton onClick={this.submitEditedPlannedLog} size="small" disabled={!this.canSubmit()} sx={{ color: 'success.main' }}>
						<SaveIcon sx={{ fontSize: 18 }} />
					</IconButton>
					{this.renderCommentButton(plannedLog.comment, plannedLog.id)}
					<IconButton onClick={() => this.deletePlannedLog(plannedLog)} size="small" sx={{ color: 'error.main' }}>
						<DeleteIcon sx={{ fontSize: 18 }} />
					</IconButton>
				</Box>
			);
		}

		return (
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, justifyContent: 'flex-end' }}>
				<IconButton onClick={(e) => this.setEditablePlannedLog(e, plannedLog)} size="small">
					<EditIcon sx={{ fontSize: 18 }} />
				</IconButton>
				{this.renderCommentButton(plannedLog.comment, plannedLog.id)}
			</Box>
		);
	}

	openInfoPopover = (e) => {
		this.setState({ infoPopoverAnchorEl: e.currentTarget });
	}

	renderInfoTooltip = () => (
		<InfoOutlinedIcon
			onClick={this.openInfoPopover}
			sx={{ fontSize: 16, color: 'text.disabled', cursor: 'pointer', ml: 0.5 }}
		/>
	)

	render() {
		const hasLogs = this.state.plannedLogs && this.state.plannedLogs.length > 0;
		const showAddRow = this.state.isNewPlannedLogBeingAdded ||
			(this.state.plannedLogs && this.state.plannedLogs.length === 0);

		return (
			<>
				{/* Comment popover */}
				<Popover
					open={this.state.dropDownCommentAnchorEl !== null}
					anchorEl={this.state.dropDownCommentAnchorEl}
					onClose={this.closeDropDownCommentAnchorEl}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
					transformOrigin={{ vertical: 'top', horizontal: 'center' }}
					BackdropComponent={Backdrop}
					BackdropProps={{ timeout: 500 }}
					PaperProps={{ sx: { borderRadius: 2.5, width: 280 } }}
				>
					<Box sx={{ p: 2 }}>
						<Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11, display: 'block', mb: 1 }}>
							Set Note
						</Typography>
						<TextField
							placeholder="e.g. slow eccentric, pause at bottom..."
							multiline
							rows={3}
							variant="outlined"
							fullWidth
							value={this.state.logComment}
							onChange={this.updateLogComment}
							size="small"
						/>
					</Box>
				</Popover>

				<Box sx={{ width: '100%' }}>
					<Fade in={this.state.isLoading}>
						<LinearProgress color="secondary" />
					</Fade>

					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell align="left" sx={{ fontWeight: 600, fontSize: 12, color: 'text.secondary', py: 1 }}>
										<Box sx={{ display: 'flex', alignItems: 'center' }}>
											Set
											{this.renderInfoTooltip()}
										</Box>
									</TableCell>
									{this.state.exercise.type.attributes.map(attribute => (
										<TableCell key={"h_attr_" + attribute.key} align="center" sx={{ fontWeight: 600, fontSize: 12, color: 'text.secondary', py: 1 }}>
											{attribute.name}
										</TableCell>
									))}
									<TableCell align="right" sx={{ py: 1 }} />
								</TableRow>
							</TableHead>

								<TableBody>
									{this.state.plannedLogs ? this.state.plannedLogs.map((plannedLog, idx) => (
										<TableRow
											key={"logs_" + plannedLog.id}
											hover
											onClick={() => this.setSelectedLog(plannedLog)}
											sx={{
												cursor: 'pointer',
												'&:last-child td': { borderBottom: 0 },
											}}
										>
											<TableCell component="th" scope="row" align="left" sx={{ py: 1 }}>
												<Box sx={{
													width: 24, height: 24, borderRadius: '50%',
													bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
													display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
												}}>
													<Typography variant="caption" sx={{ fontWeight: 700, fontSize: 12 }}>
														{idx + 1}
													</Typography>
												</Box>
											</TableCell>

											{this.state.exercise.type.attributes.map((attribute, attrIdx) => (
												<TableCell key={"attr_" + attribute.key} align="center" padding="none" sx={{ py: 1 }}>
													{this.state.editablePlannedLogId === plannedLog.id ? (
														this.renderInputField(attribute)
													) : (
														<Typography variant="body2" sx={{ fontWeight: 500 }}>
															{AttributeValuesPrettier.prettifyLogValueForAttribute(attribute.key, plannedLog.values[attrIdx])}
														</Typography>
													)}
												</TableCell>
											))}

											<TableCell align="right" sx={{ py: 0.5 }}>
												{this.renderActionButtons(plannedLog)}
											</TableCell>
										</TableRow>
									)) : null}

									{showAddRow && (
										<TableRow key="new_log" sx={{ '& td': { borderBottom: 0 } }}>
											<TableCell component="th" scope="row" align="left" sx={{ py: 1 }}>
												<Box sx={{
													width: 24, height: 24, borderRadius: '50%',
													bgcolor: (theme) => `${theme.palette.secondary.main}18`,
													display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
												}}>
													<Typography variant="caption" sx={{ fontWeight: 700, fontSize: 12, color: 'secondary.main' }}>
														{(this.state.plannedLogs?.length ?? 0) + 1}
													</Typography>
												</Box>
											</TableCell>

											{this.state.exercise.type.attributes.map(attribute => (
												<TableCell key={"new_attr_" + attribute.key} align="center" padding="none" sx={{ py: 1 }}>
													{this.renderInputField(attribute)}
												</TableCell>
											))}

											<TableCell align="right" sx={{ py: 0.5 }}>
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, justifyContent: 'flex-end' }}>
													{this.renderCommentButton(this.state.logComment, null)}
													<IconButton size="small" onClick={this.submitNewPlannedLog} disabled={!this.canSubmit()} sx={{ color: 'success.main' }}>
														<SaveIcon sx={{ fontSize: 18 }} />
													</IconButton>
												</Box>
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>

					{!showAddRow && (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
							<Button
								onClick={this.enableNewPlannedLogBeingAdded}
								size="small"
								startIcon={<AddIcon />}
								color="secondary"
								sx={{ textTransform: 'none', fontWeight: 600 }}
							>
								Add Set
							</Button>
						</Box>
					)}
				</Box>

				{this.state.infoPopoverAnchorEl && (
					<Popover
						open={this.state.infoPopoverAnchorEl !== null}
						anchorEl={this.state.infoPopoverAnchorEl}
						onClose={() => this.setState({ infoPopoverAnchorEl: null })}
						anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
						transformOrigin={{ vertical: 'top', horizontal: 'center' }}
					>
						<Typography variant="body2" sx={{ p: 1.5, maxWidth: 260 }}>
							Plan your sets ahead of time. During your workout, planned sets appear below the logger with a one-tap Log button to quickly log each set.
						</Typography>
					</Popover>
				)}
			</>
		);
	}
}

export default WorkoutPlanCard;
